// Shared bank workflow modals. Loaded after ui.jsx and before the bank pages.

const bankWorkflowNumber = (value, fallback = 0) => {
  return window.PlummeMoneyInput.number(value, fallback);
};
const bankWorkflowKr = (value) => `${bankWorkflowNumber(value) < 0 ? '−' : ''}${Math.abs(bankWorkflowNumber(value)).toLocaleString(PlummeI18n.intlLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr`;
const bankWorkflowIsoDate = (value) => {
  const text = String(value || '');
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const match = text.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  return match ? `${match[3]}-${match[2].padStart(2, '0')}-${match[1].padStart(2, '0')}` : '';
};
const bankWorkflowFiniteNumber = (value) => {
  if (value == null || String(value).trim() === '') return null;
  const number = window.PlummeMoneyInput?.number ? window.PlummeMoneyInput.number(value, NaN) : Number(String(value).replace(',', '.'));
  return Number.isFinite(number) ? number : null;
};
const bankWorkflowVatRateLabel = (post, raw, proposal) => (
  post?.vatRate == null ? (raw.vatRate == null ? proposal?.vat || '0 %' : `${raw.vatRate} %`) : `${post.vatRate} %`
);
const bankWorkflowBookedVatAmount = (post, line) => {
  const raw = post?._backend || {};
  const explicitAmount = [
    post?.vatAmount,
    raw.vatAmount,
    raw.amountVat,
    raw.bookedVatAmount,
  ].map(bankWorkflowFiniteNumber).find((value) => value != null);
  if (explicitAmount != null) return Math.abs(explicitAmount);
  const purchaseLines = safeArray(raw.purchaseLines || post?.purchaseLines);
  const lineVat = purchaseLines
    .map((purchaseLine) => bankWorkflowFiniteNumber(purchaseLine?.vatAmount))
    .filter((value) => value != null)
    .reduce((sum, value) => sum + Math.abs(value), 0);
  if (lineVat > 0) return lineVat;
  const rate = bankWorkflowFiniteNumber(post?.vatRate ?? raw.vatRate);
  if (!rate || String(post?.reverseChargeCode || raw.reverseChargeCode || '') === '86') return null;
  const lineNet = purchaseLines
    .map((purchaseLine) => bankWorkflowFiniteNumber(purchaseLine?.amountNet))
    .filter((value) => value != null)
    .reduce((sum, value) => sum + Math.abs(value), 0);
  const net = bankWorkflowFiniteNumber(post?.amountNet ?? raw.amountNet) ?? (lineNet > 0 ? lineNet : null);
  if (net != null) return Math.abs(net) * rate / 100;
  const gross = bankWorkflowFiniteNumber(post?.amount ?? raw.amount ?? line?.amount);
  return gross != null ? Math.abs(gross) * rate / (100 + rate) : null;
};
const bankWorkflowVatDisplay = (post, raw, proposal, line) => {
  const rateLabel = bankWorkflowVatRateLabel(post, raw, proposal);
  const vatAmount = bankWorkflowBookedVatAmount(post, line);
  return vatAmount != null ? `${rateLabel} · ${bankWorkflowKr(vatAmount)}` : rateLabel;
};

// Flervalg pa banklinjer. Shift-klikk merker et intervall fra forrige klikk,
// vanlig klikk merker enkeltvis. Listen som sendes inn er den synlige lista, sa
// et intervall aldri strekker seg over linjer brukeren ikke ser.
const useBankLineSelection = () => {
  const [selected, setSelected] = useState(() => new Set());
  const [lastId, setLastId] = useState(null);
  const toggle = (event, line, index, list) => {
    event.stopPropagation();
    setSelected((current) => {
      const next = new Set(current);
      if (event.shiftKey && lastId != null) {
        const from = list.findIndex((entry) => entry.id === lastId);
        if (from > -1) {
          const [start, end] = from < index ? [from, index] : [index, from];
          for (let i = start; i <= end; i += 1) next.add(list[i].id);
          return next;
        }
      }
      if (next.has(line.id)) next.delete(line.id); else next.add(line.id);
      return next;
    });
    setLastId(line.id);
  };
  const clear = () => { setSelected(new Set()); setLastId(null); };
  return { selected, toggle, clear };
};

// Ko av valgte linjer som behandles én om gangen i registreringsmodalen.
const useBankBulkQueue = () => {
  const [queue, setQueue] = useState([]);
  const [index, setIndex] = useState(0);
  const [label, setLabel] = useState('');
  const reset = () => { setQueue([]); setIndex(0); setLabel(''); };
  const start = (ids, nextLabel) => { if (!ids.length) return false; setQueue(ids); setIndex(0); setLabel(nextLabel || ''); return true; };
  // Returnerer true nar siste linje er behandlet, slik at kalleren kan kvittere.
  const advance = () => { if (index + 1 >= queue.length) { reset(); return true; } setIndex(index + 1); return false; };
  return { queue, index, label, start, advance, reset, activeId: queue[index] || '', total: queue.length };
};

// Vises mens et bilag er valgt og venter pa en banklinje. Den svever overst pa
// skjermen sa den folger med under rulling, uten a ta plass i lista.
function BankPickBar({ receiptName, onCancel }) {
  return <div className="bank-pick-bar" role="status">
    <span className="icon-chip"><Icon name="fileText" size={16}/></span>
    <div className="grow" style={{ minWidth: 0 }}><b>{t('bwf.pickChooseLine')}</b><div className="t-sm">{receiptName || t('bwf.pickedVoucher')}</div></div>
    <Button size="sm" variant="secondary" onClick={onCancel}>{t('bwf.cancelPick')}</Button>
  </div>;
}

function BankLineSelectBox({ status, selected, onToggle }) {
  return <span className={'bank-tick bank-tick-cb ' + (status || 'ny')} role="checkbox" aria-checked={selected} tabIndex={0}
    onClick={onToggle}
    onKeyDown={(event) => { if (event.key === ' ' || event.key === 'Enter') { event.preventDefault(); onToggle(event); } }}
    data-help-title={t('bwf.selectLine')} data-help={t('bwf.selectLineHelp')}>
    <span className="bank-tick-box">{selected && <Icon name="check" size={13} stroke={3}/>}</span>
  </span>;
}

function BankBulkBar({ count, onClear, children }) {
  return <div className="bank-bulk-bar">
    <span className="icon-chip"><Icon name="check" size={16}/></span>
    <div className="grow" style={{ minWidth: 0 }}><b>{count} {t('bwf.selectedLines')}</b><div className="t-sm">{t('bwf.selectedLinesHelp')}</div></div>
    {children}
    <Button size="sm" variant="ghost" onClick={onClear}>{t('bwf.clearSelection')}</Button>
  </div>;
}

function BankMissingReceiptModal({ line, onClose, onConfirm }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async () => {
    const cleanReason = reason.trim();
    if (cleanReason.length < 3) { setError(t('bank.voucherLostReasonRequired')); return; }
    setBusy(true);
    setError('');
    try { await onConfirm(cleanReason); }
    catch (err) { setError(err?.message || t('bank.voucherLostError')); }
    finally { setBusy(false); }
  };
  return <Modal open onClose={busy ? () => {} : onClose} title={t('bank.markVoucherLostTitle')} max={540}
    footer={<React.Fragment>
      <Button variant="ghost" disabled={busy} onClick={onClose}>{t('common.cancel')}</Button>
      <Button icon="check" disabled={busy || reason.trim().length < 3} onClick={submit} data-help={t('helpTopics.lostEvidence.body')} data-help-title={t('helpTopics.lostEvidence.title')}>{busy ? t('bank.markVoucherLostSaving') : t('bank.markVoucherLostConfirm')}</Button>
    </React.Fragment>}>
    <div className="col gap-14">
      <div className="bank-resolution-summary"><div><span>{line?.date || line?._backend?.date}</span><b>{line?.title || line?._backend?.description || t('bank.bankLine', 'Banklinje')}</b></div><strong className="neg">{bankWorkflowKr(line?.amount)}</strong></div>
      <div className="bg-note warn"><Icon name="alertTri" size={18}/><span>{t('bank.markVoucherLostHelp')}</span></div>
      <Field label={t('bank.voucherLostReason')} hint={t('bank.voucherLostReasonHint')} help={t('helpTopics.lostEvidence.body')} helpTitle={t('helpTopics.lostEvidence.title')}>
        <textarea className="input" rows="3" value={reason} onChange={(event) => setReason(event.target.value)} placeholder={t('bank.voucherLostReasonPlaceholder')} autoFocus />
      </Field>
      {/* Forklaringen er den samme her som i banklinjemodalen, saa forslagene er
          det ogsaa. Uten dem maatte den vanligste setningen skrives for haand
          hver gang — og et paakrevd felt som er tungt aa fylle ut blir fylt ut
          daarlig. */}
      <BankDocumentationReasonChips value={reason} onChange={setReason} />
      {error && <div className="bg-note bad"><Icon name="alertTri" size={18}/><span>{error}</span></div>}
    </div>
  </Modal>;
}

// etter denne fila — oppslaget skjer derfor forst ved render.
function BankPostingResolutionModal({ post, onClose, onConfirm, onCorrect, initialChoice = '', allowCorrect = true, annulAsIgnore = false }) {
  const raw = post?._backend || post || {};
  const entryType = String(post?.entryType || raw.entryType || '');
  const canCorrect = ['bank-posting', 'manual-posting', 'expense', 'other-sale'].includes(entryType);
  const receiptName = post?.attachmentName || raw.attachmentName || raw.receiptName || '';
  const [choice, setChoice] = useState(initialChoice);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const choices = [
    ['annul', 'trash', annulAsIgnore ? t('bank.ignoreLine') : t('bwf.annulEntry'), annulAsIgnore ? t('bank.ignoreLineHelp') : t('bank.resolve.annulText')],
    ...(canCorrect && allowCorrect ? [['correct_entry', 'pencil', t('bwf.correctEntry'), t('bank.resolve.correctText')]] : []),
  ];
  const choose = (value) => {
    if (value === 'correct_entry') { onCorrect?.(post); return; }
    setChoice(value);
    setError('');
  };
  const submitAnnulment = async () => {
    const cleanReason = reason.trim();
    if (cleanReason.length < 3) { setError(t('bank.corr.err.reasonVoid')); return; }
    setBusy(true);
    setError('');
    try { await onConfirm({ resolution: 'annul', reason: cleanReason }); }
    catch (err) { setError(err?.message || t('bank.resolve.err.voidFailed')); }
    finally { setBusy(false); }
  };
  return <Modal open onClose={busy ? () => {} : onClose} title={t('bwf.whatToDo')} max={620}
    footer={<><Button variant="ghost" disabled={busy} onClick={onClose}>{t('common.cancel')}</Button>{choice === 'annul' && <Button icon="check" disabled={busy || reason.trim().length < 3} onClick={submitAnnulment}>{busy ? t('bank.resolve.voiding') : annulAsIgnore ? t('bank.ignoreLineConfirm') : t('bank.resolve.confirmVoid')}</Button>}</>}>
    <div className="col gap-12 bank-resolution-modal">
      <div className="col gap-8">{choices.map(([value, icon, title, text]) => <button type="button" key={value} className={'bank-resolution-choice' + (choice === value ? ' selected' : '')} onClick={() => choose(value)}><span className="icon-chip"><Icon name={icon} size={17}/></span><span><b>{title}</b><small>{text}</small></span><i>{choice === value && <Icon name="check" size={14}/>}</i></button>)}</div>
      {choice === 'annul' && <Field label={annulAsIgnore ? t('bank.ignoreLineReason') : t('bank.resolve.voidReason')} hint={annulAsIgnore ? t('bank.ignoreLineReasonHint') : t('bank.resolve.voidReasonHint')} help={annulAsIgnore ? t('helpTopics.ocrCorrection.body') : t('helpTopics.reversal.body')} helpTitle={annulAsIgnore ? t('helpTopics.ocrCorrection.title') : t('helpTopics.reversal.title')}><textarea className="input" rows="3" value={reason} onChange={(event) => setReason(event.target.value)} placeholder={annulAsIgnore ? t('bank.ignoreLineReasonPlaceholder') : t('bank.resolve.voidReasonPh')} autoFocus /></Field>}
      {choice === 'annul' && receiptName && <div className="bg-note info"><Icon name="inbox" size={17}/><span>{t('bank.resolve.voucherReturns', { name: receiptName })}</span></div>}
      {error && <div className="bg-note bad"><Icon name="alertTri" size={18}/><span>{error}</span></div>}
    </div>
  </Modal>;
}

function BankBookedPostModal({ line, post, onClose, onFixPost, onReopenInvestmentMatch }) {
  const raw = line?._backend || {};
  const proposal = typeof bgProposal === 'function' ? bgProposal(line) : null;
	  const entryType = String(post?.entryType || raw.entryType || '');
	  const canCorrect = ['bank-posting', 'manual-posting', 'expense', 'other-sale'].includes(entryType);
	  const bankMatchGroupId = String(post?.bankMatchGroupId || post?._backend?.bankMatchGroupId || '');
	  const canReversePayment = entryType === 'payment' && Boolean(bankMatchGroupId);
	  const canResolvePost = Boolean(post?.id && (canCorrect || canReversePayment) && onFixPost);
	  const isInvestmentPosting = post?.source === 'investment' || post?._backend?.source === 'investment' || String(raw.matchedEntryId || '').startsWith('investment-');
	  const canReopenInvestmentMatch = Boolean(raw.matchedEntryId && isInvestmentPosting && onReopenInvestmentMatch);
	  const canReversePurchase = entryType === 'expense' || raw.matchedEntryType === 'expense';
  const isExistingEntry = raw.bookingType === 'existing-entry' || raw.matchedEntryType === 'expense' || raw.matchedEntryType === 'invoice' || raw.matchedEntryType === 'other-sale';
  const receiptName = line?.bilagName || raw.attachmentName || post?.attachmentName || '';
  const rows = [
    [t('bank.resolve.row.postedEntry'), post?.title || raw.matchedEntryTitle || proposal?.match || raw.note || line?.title],
    [t('bank.resolve.row.account'), post?.counterAccountCode || raw.accountCode || proposal?.account || '—'],
    [t('bank.resolve.row.vat'), bankWorkflowVatDisplay(post, raw, proposal, line)],
    [t('bank.resolve.row.amount'), bankWorkflowKr(post?.amount ?? line?.amount)],
    [t('bank.resolve.row.lineDate'), line?.date],
    [t('bank.resolve.row.bankLine'), line?.title],
    [t('bank.resolve.row.voucher'), receiptName || t('bank.resolve.row.notLinked')],
  ];
  return <Modal open onClose={onClose} title={t('bwf.postedEntry')} max={620} titleRight={<span className="pp pp-paid">{t('bwf.matched')}</span>}
	    footer={<><Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>{canReopenInvestmentMatch && <Button variant="secondary" icon="unlink" onClick={() => onReopenInvestmentMatch(line)}>{t('bank.resolve.releaseInvestmentMatch')}</Button>}{canResolvePost && <Button icon={canCorrect ? 'pencil' : 'refresh'} onClick={() => { onFixPost(post); onClose(); }} data-help={t('helpTopics.correction.body')} data-help-title={t('helpTopics.correction.title')}>{canCorrect ? t('bank.fixUp') : t('bank.resolve.reverse')}</Button>}</>}>
    <div className="col gap-14">
      <div className="bg-note ok"><Icon name="checkCircle" size={18}/><span>{t('bank.resolve.alreadyPosted')}</span></div>
      {canResolvePost
	        ? <div className="bg-note info"><Icon name={canCorrect ? 'pencil' : 'refresh'} size={18}/><span>{t(canCorrect ? 'bank.resolve.useCorrection' : 'bank.resolve.reversePaymentInfo')}</span></div>
        : <div className="bg-note info"><Icon name={canReversePurchase ? 'refresh' : 'fileText'} size={18}/><span>{canReversePurchase ? t('bank.resolve.reverseInfo') : isExistingEntry ? t('bank.resolve.existingInfo') : t('bank.resolve.verifiableInfo')}</span></div>}
      <div className="bank-booked-post-table">{rows.map(([label, value]) => <div key={label}><span>{label}</span><b>{value || '—'}</b></div>)}</div>
    </div>
  </Modal>;
}

function BankSuggestionConfirmModal({ lines, onClose, onConfirm, busy }) {
  const linesPage = usePaged(lines, 10);
  const setLinesPageSize = (size) => { linesPage.setPs(size); linesPage.setPage(1); };
  return <Modal open onClose={busy ? () => {} : onClose} title={t('bank.useSuperPlumma')} max={620}
    footer={<><Button variant="ghost" disabled={busy} onClick={onClose}>{t('home.donut.back')}</Button><Button icon="sparkle" disabled={busy} onClick={onConfirm}>{busy ? t('bank.resolve.approving') : t('bank.resolve.approveN', { count: lines.length })}</Button></>}>
    <div className="col gap-14"><div className="bg-note info"><Icon name="sparkle" size={18}/><span>{t('bank.resolve.confirmIntro')}</span></div><div className="col gap-8">{linesPage.slice.map((line) => { const proposal = typeof bgProposal === 'function' ? bgProposal(line) : null; return <div className="bank-confirm-line" key={line.id}><div><b>{line.title}</b><span>{proposal?.action || line.sub}</span></div><strong className={line.amount < 0 ? 'neg' : 'pos'}>{bankWorkflowKr(line.amount)}</strong></div>; })}</div>{linesPage.total > 0 && <Pagination {...linesPage} onPage={linesPage.setPage} pageSize={linesPage.ps} onPageSize={setLinesPageSize} pageSizes={[10, 50, 100]}/>}</div>
  </Modal>;
}

// tapMode: på berøringsflater er innboksen stablet under linjelista, og å dra et
// bilag flere skjermhøyder opp er upraktisk. Da velger man bilaget med ett trykk
// og trykker deretter på banklinjen. Drag-and-drop beholdes for mus.
// Under 1100px er det ikke plass til innboksen ved siden av linjelista. Da blir
// den en skuff bak en fane pa hoyre kant: drag-and-drop er umulig fra en skuff,
// sa der velger man bilaget med ett trykk og skuffen lukker seg selv.
// `superLabel` finnes fordi knappen ikke gjor det samme overalt. I den veiledede
// avstemmingen kobler den bilag; i full oversikt analyserer den banklinjene og
// foreslar bokforing. Sto det «SuperPlumma kobler» begge steder, lovet knappen
// noe den ikke gjorde — og et klikk i full oversikt saa ut som en kobling som
// ikke skjedde.
// Begrunnelsen for at en banklinje bokfores uten eget bilag. Den er ikke pynt:
// den er det eneste sporet en kontroll har etter hvorfor kjopet staar
// udokumentert, og feltet er derfor fortsatt paakrevd og fritt redigerbart.
//
// Men den samme setningen ble skrevet om igjen for hver linje, saa de tre
// vanligste staar som forslag. Forslagene sier hva som er tilfelle med bilaget
// — ikke at kjopet dermed er dokumentert. En banklinje alene sier ikke hva som
// ble kjopt, og fradragsretten avgjores ikke her; kontrollagenten flagger
// fortsatt kjop uten bilag.
const BANK_DOCUMENTATION_REASONS = [
  { key: 'lost', label: 'bank.voucherMarkedLost', text: 'bank.docReason.lostText' },
  { key: 'never', label: 'bank.docReason.never', text: 'bank.docReason.neverText' },
  { key: 'statementOnly', label: 'bank.docReason.statementOnly', text: 'bank.docReason.statementOnlyText' },
  { key: 'notApplicable', label: 'bank.docReason.notApplicable', text: 'bank.docReason.notApplicableText' },
];

// Selve hurtigtastene. De ligger for seg fordi de brukes to steder: under
// avhukingen i banklinjemodalen, og i «Marker bilag som borte» — samme
// begrunnelse, samme forslag, uansett hvor brukeren kom inn.
function BankDocumentationReasonChips({ value = '', onChange }) {
  const current = String(value || '').trim();
  // Et forslag som allerede staar i feltet slaas av igjen med det samme trykket.
  // Ellers ville brukeren maatte markere og slette teksten for aa skrive sin egen.
  const pick = (text) => onChange?.(current === text ? '' : text);
  return <div className="bank-doc-reasons">
    {BANK_DOCUMENTATION_REASONS.map((reason) => {
      const text = t(reason.text);
      return <button type="button" key={reason.key} className={'bank-doc-reason' + (current === text ? ' on' : '')}
        aria-pressed={current === text} onClick={() => pick(text)}>{t(reason.label)}</button>;
    })}
  </div>;
}

function BankDocumentationReasonField({ value = '', onChange, placeholder = '' }) {
  return <div className="col gap-8">
    <input className="input" value={value} placeholder={placeholder} onChange={(event) => onChange?.(event.target.value)} />
    <BankDocumentationReasonChips value={value} onChange={onChange} />
  </div>;
}

// Hvilke bilag innboksen ved siden av banklinjene skal tilby. Regelen bodde i
// tre kopier — panelet, den sammenslaatte skinnen og den veiledede
// avstemmingen — og de tre var ikke enige: et koblet bilag som trengte kontroll
// ble talt av maanedskontrollen og skjult av panelet, som da sa «Alle bilag er
// koblet» mens fanen sa «1 bilag».
//
// Regelen er naa den SAMME som fanen bruker, og ikke en variant av den: skjul et
// bilag naar det er koblet og det ikke er noe mer aa si om koblingen. At bilaget
// kanskje er en dobbeltopplasting eller likner en post som alt er bokfort, er
// nettopp grunnen til at det skal staa framme — begge steder, med samme tall.
//
// `bankLinkedResolved` er svaret fra `receiptBankLinkedResolved`, hentet fra
// innboksmodellen i `buildBankPageModel`. Er feltet ikke satt — en kaller som
// ikke gaar gjennom den modellen — gjelder den gamle regelen: koblet er koblet.
const bankInboxReceiptAvailable = (receipt) => {
  if (receipt?._backend?.status === 'booked') return false;
  if (receipt?._backend?.status === 'linked-supporting-document') return false;
  if (receipt?.bankLinkedResolved === undefined) return !receipt?.linkedBankLine;
  return receipt.bankLinkedResolved !== true;
};
const bankInboxAvailableReceipts = (inbox = []) =>
  (inbox || []).filter((receipt) => bankInboxReceiptAvailable(receipt));

Object.assign(window, { BankPostingResolutionModal, BankBookedPostModal, BankSuggestionConfirmModal, BankMissingReceiptModal, BankLineSelectBox, BankBulkBar, BankPickBar, useBankLineSelection, useBankBulkQueue, BankDocumentationReasonField, BankDocumentationReasonChips, BANK_DOCUMENTATION_REASONS, bankInboxReceiptAvailable, bankInboxAvailableReceipts });
